import React, { useCallback, useEffect, useState } from "react";
import { Trash2, Undo2, Loader2, AlertTriangle } from "lucide-react";
import api from "../../api";
import { useToast } from "../../hooks/use-toast";

const when = (iso) => String(iso || "").slice(0, 16).replace("T", " ");
const fmtSize = (b) => {
  const n = Number(b || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(2)} MB`;
};

// The recycle bin: admin-deleted deals kept for a few days so a mistaken delete can be undone.
export const AdminRecycleBin = () => {
  const { toast } = useToast();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => { api.get("/admin/deleted-deals").then((r) => setD(r.data)).catch(() => setD(null)); }, []);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;

  const restore = async (deal) => {
    setBusy(deal.id);
    try {
      await api.post(`/admin/deals/${deal.id}/undelete`);
      toast({ title: "Deal restored", description: deal.title || "" });
      load();
    } catch (e) { toast({ title: "Could not restore", description: e.response?.data?.detail || "It may already be purged." }); }
    finally { setBusy(""); }
  };

  const purge = async (deal) => {
    if (!window.confirm(`Permanently delete “${deal.title || "this deal"}”?\n\nThis can't be undone.`)) return;
    setBusy(deal.id);
    try {
      await api.delete(`/admin/deleted-deals/${deal.id}`);
      toast({ title: "Permanently deleted" });
      load();
    } catch (e) { toast({ title: "Could not delete", description: e.response?.data?.detail || "Try again." }); }
    finally { setBusy(""); }
  };

  const empty = async () => {
    if (!window.confirm(`Empty the recycle bin?\n\nThis permanently deletes all ${d.count} item(s). Nothing here can be restored afterwards.`)) return;
    setBusy("empty");
    try {
      const { data } = await api.post("/admin/deleted-deals/empty");
      toast({ title: "Recycle bin emptied", description: `${data.purged} item(s) permanently deleted.` });
      load();
    } catch (e) { toast({ title: "Could not empty", description: e.response?.data?.detail || "Try again." }); }
    finally { setBusy(""); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-recycle-bin">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-bold text-foreground flex items-center gap-2 flex-1">
          <Trash2 size={18} className="text-[#16a34a]" /> Recycle bin
        </h2>
        <span className="text-xs text-muted-foreground">kept {d.graceDays} days, then purged automatically</span>
        <button onClick={empty} disabled={busy === "empty" || d.count === 0} data-testid="recycle-empty-btn"
          className="inline-flex items-center gap-1.5 text-xs font-bold bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white rounded-xl px-3 py-1.5 transition-colors">
          {busy === "empty" ? <Loader2 className="animate-spin" size={13} /> : <Trash2 size={13} />} Empty bin now
        </button>
      </div>
      <p className="text-xs text-muted-foreground mt-0.5 mb-4">
        Deals an admin deleted. Restore one if it was a mistake, or clear them out for good.
        {" "}<span className="font-semibold text-foreground" data-testid="recycle-total-size">
          {d.count} item{d.count === 1 ? "" : "s"} · {fmtSize(d.totalBytes)}
        </span> in the bin.
      </p>

      {d.count === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="recycle-empty-state">The recycle bin is empty.</p>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-auto pr-1" data-testid="recycle-list">
          {d.deals.map((deal) => (
            <div key={deal.id} className="flex items-center gap-3 text-sm border-b border-border/50 pb-1.5 last:border-0"
              data-testid={`recycle-row-${deal.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-foreground truncate">{deal.title || "Untitled deal"}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {deal.business || "—"} · deleted {when(deal.deletedAt)}{deal.deletedBy ? ` by ${deal.deletedBy}` : ""}
                </p>
              </div>
              <span className={`text-xs shrink-0 inline-flex items-center gap-1 ${deal.daysLeft <= 1 ? "text-rose-600 font-bold" : "text-muted-foreground"}`}>
                {deal.daysLeft <= 1 && <AlertTriangle size={12} />}{deal.daysLeft}d left
              </span>
              <span className="text-xs text-muted-foreground shrink-0 w-16 text-right tabular-nums" data-testid={`recycle-size-${deal.id}`}>{fmtSize(deal.bytes)}</span>
              <button onClick={() => restore(deal)} disabled={busy === deal.id} data-testid={`recycle-restore-${deal.id}`}
                className="text-[#16a34a] hover:underline text-xs font-semibold inline-flex items-center gap-1 shrink-0 disabled:opacity-60">
                {busy === deal.id ? <Loader2 className="animate-spin" size={12} /> : <Undo2 size={12} />} Restore
              </button>
              <button onClick={() => purge(deal)} disabled={busy === deal.id} data-testid={`recycle-delete-${deal.id}`}
                className="text-rose-600 hover:underline text-xs font-semibold inline-flex items-center gap-1 shrink-0 disabled:opacity-60">
                <Trash2 size={12} /> Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminRecycleBin;
